import { ApiClient } from "~/api/api-client";

/**
 * Config del antibot en runtime (`GET /api/auth/bot-check/config`): si está prendido, las dos
 * sitekeys (la del nivel invisible y la del nivel con clic) y cuánto esperar el token invisible.
 *
 * Viene de la API y no de una variable de build para poder cambiar una sitekey, o apagar el nivel
 * con clic, sin recompilar la web. `VITE_TURNSTILE_SITE_KEY` queda como respaldo por si la config
 * no llega: con eso el nivel invisible sigue andando como antes.
 */

export type BotCheckConfig = {
  /** false: la API no verifica nada, así que no se monta ningún widget. */
  enabled: boolean;
  invisibleSiteKey: string | null;
  /** null: no hay nivel con clic, nunca se escala. */
  managedSiteKey: string | null;
  /** Cuánto se espera el token invisible, contado desde que se pidió el widget. */
  graceMs: number;
};

export const DEFAULT_GRACE_MS = 9000;
const MAX_GRACE_MS = 60_000;

/**
 * Cuánto se espera la config antes de seguir con el respaldo. Corta: quien llega al botón antes
 * de que responda ya está esperando, y el respaldo alcanza para el nivel invisible.
 */
const CONFIG_TIMEOUT_MS = 4000;

const BUILD_SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim();

/** Lo que se usa cuando la config no llega: el comportamiento de antes de tenerla. */
export function fallbackBotCheckConfig(): BotCheckConfig {
  return {
    enabled: Boolean(BUILD_SITE_KEY),
    invisibleSiteKey: BUILD_SITE_KEY || null,
    managedSiteKey: null,
    graceMs: DEFAULT_GRACE_MS,
  };
}

const siteKey = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

/** Valida la respuesta en vez de confiar en ella: un campo raro no puede dejar el form colgado. */
export function normalizeBotCheckConfig(raw: unknown): BotCheckConfig | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Record<string, unknown>;
  if (typeof data.enabled !== "boolean") return null;
  const grace = Number(data.graceMs);
  return {
    enabled: data.enabled,
    // Prendido pero sin sitekey invisible: la API pide usar la de build.
    invisibleSiteKey: siteKey(data.invisibleSiteKey) ?? BUILD_SITE_KEY ?? null,
    managedSiteKey: siteKey(data.managedSiteKey),
    graceMs:
      Number.isFinite(grace) && grace >= 0
        ? Math.min(Math.round(grace), MAX_GRACE_MS)
        : DEFAULT_GRACE_MS,
  };
}

let cached: Promise<BotCheckConfig> | null = null;

async function fetchConfig(): Promise<BotCheckConfig> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), CONFIG_TIMEOUT_MS);
  });
  try {
    const response = await Promise.race([
      ApiClient.api.authControllerGetAuthBotCheckConfig(),
      timeout,
    ]);
    const config = normalizeBotCheckConfig(response?.data?.data);
    if (config) return config;
  } catch {
    // Red caída, 5xx o un deploy viejo sin el endpoint: se sigue con el respaldo.
  } finally {
    clearTimeout(timer);
  }
  // No se guarda el respaldo: el próximo formulario que se abra vuelve a preguntar.
  cached = null;
  return fallbackBotCheckConfig();
}

/** Una sola consulta por carga de página (la config es la misma para todos los formularios). */
export function loadBotCheckConfig(): Promise<BotCheckConfig> {
  if (!cached) cached = fetchConfig();
  return cached;
}

/** Sólo para tests. */
export function __resetBotCheckConfigForTests() {
  cached = null;
}
